import type { TFunction } from "i18next";
import { Funnel } from "lucide-react";

import { labelFor } from "../hooks/useDistributionSlices";

import { Button } from "@/components/ui/v2/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/v2/dropdown-menu";
import type { ActiveCampaignDistributionItem } from "@/features/home/api/useActiveCampaignDistribution";

interface Props {
  distribution: ActiveCampaignDistributionItem[];
  filterActive: boolean;
  isVisible: (name: string) => boolean;
  toggle: (name: string) => void;
  reset: () => void;
  numberFormatter: Intl.NumberFormat;
  t: TFunction;
  i18n: { exists: (k: string) => boolean; language: string };
}

export function DistributionFilterMenu({
  distribution,
  filterActive,
  isVisible,
  toggle,
  reset,
  numberFormatter,
  t,
  i18n,
}: Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label={t("home.activeCampaignDistribution.filter.aria")}
          className="relative shrink-0"
        >
          <Funnel className="size-4" />
          {t("common.filterSelection")}
          {filterActive && (
            <span
              aria-hidden
              className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-primary"
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 max-h-80">
        <div className="flex items-center justify-between gap-2 px-2 py-1">
          <span className="text-sm font-semibold">
            {t("home.activeCampaignDistribution.filter.title")}
          </span>
          {filterActive && (
            <Button type="button" onClick={reset} variant="link" size="sm">
              {t("home.activeCampaignDistribution.filter.reset")}
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />
        {distribution.map((item) => (
          <DropdownMenuCheckboxItem
            key={item.integrationId}
            checked={isVisible(item.name)}
            onCheckedChange={() => toggle(item.name)}
            onSelect={(e: Event) => e.preventDefault()}
            count={numberFormatter.format(item.campaignCount)}
          >
            {labelFor(item, t, i18n)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
